import React, { useCallback, useMemo } from 'react';
import { FieldLabel } from '@puckeditor/core';

import { isHexColor, hexColorSwatchValue } from '../hex-color';
import { HexColorError, useHexColorInput } from './use-hex-color-input';
import { fieldControlStyle } from './field-control-style';

type ColorFieldProps = {
  field: { label?: string };
  name: string;
  value: string | null | undefined;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

/** Extracts RGB and alpha for the native swatch and opacity controls. */
function parseColor(value: string): { hex: string; alpha: number } {
  const hex = hexColorSwatchValue(value, '#000000');
  if (!isHexColor(value)) return { hex, alpha: 1 };
  let alphaHex = 'ff';
  if (value.length === 5) alphaHex = `${value[4]}${value[4]}`;
  if (value.length === 9) alphaHex = value.slice(7, 9);
  return { hex, alpha: Math.round((parseInt(alphaHex, 16) / 255) * 100) / 100 };
}

/** Combines a 6-digit hex and alpha (0-1) into the stored color value. */
function toColorValue(hex: string, alpha: number): string {
  if (alpha >= 1) return hex;
  const alphaHex = Math.round(alpha * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${alphaHex}`;
}

/** Puck custom field widget: hex color input with opacity slider. */
export function ColorField({ field, name, value, onChange, readOnly }: ColorFieldProps): JSX.Element {
  const hexInput = useHexColorInput(value, onChange);
  const { setColor } = hexInput;
  const { hex, alpha } = useMemo(() => parseColor(value ?? ''), [value]);
  // An empty draft is neutral only while the stored value is also unset; clearing a saved color still flags it.
  const invalid = (hexInput.text !== '' || (value ?? '') !== '') && hexInput.invalid;

  const handleAlphaChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const newAlpha = parseFloat(e.currentTarget.value);
      setColor(toColorValue(hex, newAlpha));
    },
    [setColor, hex]
  );

  return (
    <FieldLabel label={field.label ?? name} readOnly={readOnly}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <input
          type='color'
          value={hex}
          onChange={hexInput.handleSwatchChange}
          disabled={readOnly}
          data-testid={`color-swatch-${name}`}
          style={{ width: 32, height: 32, padding: 0, border: '1px solid #d0d4d8', borderRadius: 4, cursor: 'pointer' }}
        />
        <input
          type='text'
          value={hexInput.text}
          onChange={hexInput.handleChange}
          disabled={readOnly}
          aria-label={`${field.label ?? name} custom color value`}
          aria-invalid={invalid || undefined}
          aria-describedby={invalid ? hexInput.errorId : undefined}
          data-testid={`color-text-${name}`}
          style={{
            ...fieldControlStyle,
            flex: 1,
            minWidth: 0,
            fontFamily: 'monospace',
          }}
        />
      </div>
      {invalid && <HexColorError id={hexInput.errorId} />}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 6 }}>
        <input
          type='range'
          min='0'
          max='1'
          step='0.01'
          value={alpha}
          onChange={handleAlphaChange}
          disabled={readOnly}
          data-testid={`color-alpha-${name}`}
          style={{ flex: 1 }}
        />
        <span style={{ fontSize: 12, color: '#646d72', minWidth: 36, textAlign: 'right' }}>
          {Math.round(alpha * 100)}%
        </span>
      </div>
    </FieldLabel>
  );
}
